import React, { useState, useEffect } from 'react';
import { config } from '../config';
import './BillFollowButton.css';

interface BillFollowButtonProps {
  billId: string;
  jwtToken: string | null;
  isSubscribed: boolean;
  onSubscriptionChange?: (isSubscribed: boolean) => void;
}

const BillFollowButton: React.FC<BillFollowButtonProps> = ({ 
  billId, 
  jwtToken, 
  isSubscribed,
  onSubscriptionChange 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async () => {
    if (!jwtToken) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${config.API_BASE_URL}/bills/${billId}/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          preferences: {
            email_notifications: true,
            status_updates: true
          }
        }),
      });

      if (response.ok) {
        onSubscriptionChange?.(true);
        console.log(`Successfully subscribed to bill ${billId}`);
      } else {
        const errorData = await response.json();
        console.error('Subscription failed:', errorData);
        setError(errorData.detail || 'Failed to subscribe to bill');
      }
    } catch (error) {
      console.error('Error subscribing to bill:', error);
      setError('Failed to subscribe to bill');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!jwtToken) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${config.API_BASE_URL}/bills/${billId}/unsubscribe`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        onSubscriptionChange?.(false);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to unsubscribe from bill');
      }
    } catch (error) {
      console.error('Error unsubscribing from bill:', error);
      setError('Failed to unsubscribe from bill');
    } finally {
      setIsLoading(false);
    }
  };

  if (!jwtToken) {
    return null;
  }

  return (
    <div className="bill-follow-container">
      {error && (
        <div className="error-message" style={{ color: 'red', fontSize: '12px', marginBottom: '5px' }}>
          {error}
        </div>
      )}
      <button
        className={`follow-button ${isSubscribed ? 'subscribed' : 'not-subscribed'}`}
        onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
        disabled={isLoading}
        title={isSubscribed ? 'Unsubscribe from bill updates' : 'Subscribe to bill updates'}
      >
        {isLoading ? (
          '⏳'
        ) : isSubscribed ? (
          '🔔 Following'
        ) : (
          '🔔 Follow Bill'
        )}
      </button>
    </div>
  );
};

export default BillFollowButton;
